import React, {useEffect, useState, useRef} from 'react';
import {
  Modal,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  Text,
  ActivityIndicator,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useDispatch, useSelector} from 'react-redux';
import {
  fetchUserZoneAccesses,
  fetchCartZoneAccesses,
  updateUserZoneAccess,
  updateCartZoneAccess,
  createUserZoneAccess,
  createCartZoneAccess
} from '../../../actions/zoneActions';
import CommonStyles from '../../../style/commonStyles';
import styles from './styles';
import i18n from 'i18n-js';
import {
  getNextAccessLevel,
  getAccessLevelLabel,
  getAccessLevelGradient,
  getAccessLevelBorderColor,
  getTooltipLeft,
  getTooltipTop,
  getTooltipArrowLeft,
  extractUniqueZones,
  measureIconPosition
} from '../../../utils/zoneAccessUtils';

export const ZoneAccessModal = ({
  isVisible,
  onClose,
  entityName,
  entityId,
  entityType,
  username
}) => {
  const dispatch = useDispatch();
  const [zoneAccesses, setZoneAccesses] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [iconPosition, setIconPosition] = useState({x: 0, y: 0, width: 0, centerX: 0});
  const iconRef = useRef(null);

  const getEntityIdentifier = () => {
    if (entityType === 'user') {
      return username;
    }
    return entityId;
  };

  const entityIdentifier = getEntityIdentifier();

  useEffect(() => {
    if (isVisible) {
      if (entityIdentifier) {
        loadZoneAccesses(entityIdentifier);
      }
    } else {
      setZoneAccesses([]);
      setZones([]);
      setError('');
      setLoading(false);
      setPendingChanges({});
    }
  }, [isVisible, entityIdentifier, entityType]);

  const loadZoneAccesses = (entityIdentifierValue) => {
    if (!entityIdentifierValue) return;
    setLoading(true);
    setError('');

    const successCallback = (accesses) => {
      setZoneAccesses(accesses || []);
      const uniqueZones = extractUniqueZones(accesses || []);
      setZones(uniqueZones);
      setLoading(false);
    };

    if (entityType === 'user') {
      dispatch(fetchUserZoneAccesses(entityIdentifierValue, successCallback));
    } else {
      dispatch(fetchCartZoneAccesses(entityIdentifierValue, successCallback));
    }
  };

  const getAccessLevelForZone = zoneId => {
    if (pendingChanges[zoneId] !== undefined) {
      return pendingChanges[zoneId];
    }
    const access = zoneAccesses.find(za => {
      if (zoneId === null || zoneId === 'null') {
        return za.zone_id === null;
      }
      return za.zone_id === zoneId || za.zone_id === parseInt(zoneId);
    });
    return access?.access_level || 'disabled';
  };


  const handleAccessLevelChange = (zoneId) => {
    const currentLevel = getAccessLevelForZone(zoneId);
    const newAccessLevel = getNextAccessLevel(currentLevel);
    setPendingChanges(prev => ({
      ...prev,
      [zoneId]: newAccessLevel
    }));
  };

  const handleSave = () => {
    if (!entityIdentifier) {
      setError(i18n.t('zoneAccess.unableToIdentifyEntity'));
      return;
    }

    const changes = Object.keys(pendingChanges);
    if (changes.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError('');

    let completedSaves = 0;

    const checkAllSavesComplete = () => {
      if (completedSaves === changes.length) {
        const reloadSuccessCallback = (accesses) => {
          setZoneAccesses(accesses || []);
          const uniqueZones = extractUniqueZones(accesses || []);
          setZones(uniqueZones);
          setPendingChanges({});
          setSaving(false);
          onClose();
        };

        if (entityType === 'user') {
          dispatch(fetchUserZoneAccesses(entityIdentifier, reloadSuccessCallback));
        } else {
          dispatch(fetchCartZoneAccesses(entityIdentifier, reloadSuccessCallback));
        }
      }
    };

    changes.forEach(zoneId => {
      const newAccessLevel = pendingChanges[zoneId];
      const existingAccess = zoneAccesses.find(za => {
        if (zoneId === null || zoneId === 'null') {
          return za.zone_id === null;
        }
        const parsedZoneId = parseInt(zoneId);
        return za.zone_id === parsedZoneId || (isNaN(parsedZoneId) && za.zone_id === zoneId);
      });

      const saveSuccessCallback = () => {
        completedSaves++;
        checkAllSavesComplete();
      };

      if (existingAccess) {
        if (entityType === 'user') {
          dispatch(updateUserZoneAccess(
            entityIdentifier,
            existingAccess.id,
            newAccessLevel,
            saveSuccessCallback
          ));
        } else {
          dispatch(updateCartZoneAccess(
            entityIdentifier,
            existingAccess.id,
            newAccessLevel,
            saveSuccessCallback
          ));
        }
      } else {
        const zoneIdToCreate = (zoneId === null || zoneId === 'null') ? null : parseInt(zoneId);
        if (entityType === 'user') {
          dispatch(createUserZoneAccess(
            entityIdentifier,
            zoneIdToCreate,
            newAccessLevel,
            saveSuccessCallback
          ));
        } else {
          dispatch(createCartZoneAccess(
            entityIdentifier,
            zoneIdToCreate,
            newAccessLevel,
            saveSuccessCallback
          ));
        }
      }
    });
  };


  const renderTooltipText = () => {
    return (
      <Text style={styles.tooltipText}>
        {i18n.t('zoneAccess.alwaysOnIntro')}
        <Text style={styles.tooltipTextBold}>{i18n.t('zoneAccess.alwaysOn')}</Text>
        {i18n.t('zoneAccess.alwaysOnDescription')}
        <Text style={styles.tooltipTextBold}>{i18n.t('zoneAccess.optionalZonesLabel')}</Text>
        {i18n.t('zoneAccess.optionalZonesDescription')}
        <Text style={styles.tooltipTextBold}>{i18n.t('zoneAccess.disabledZonesLabel')}</Text>
        {i18n.t('zoneAccess.disabledZonesDescription')}
      </Text>
    );
  };

  const handleToggleTooltip = () => {
    measureIconPosition(iconRef, (position) => {
      setIconPosition(position);
    });
    setShowTooltip(!showTooltip);
  };


  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent={true}
      onRequestClose={onClose}>
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}>
        <TouchableWithoutFeedback>
          <View style={styles.modalContent}>
            <LinearGradient
              colors={['#232931', '#232931']}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 0}}
              style={styles.container}>
              <View style={styles.subContainer}>
                <View style={styles.header}>
                  <Text style={styles.title}>
                    {entityName} {i18n.t('zoneAccess.title')}
                  </Text>
                  <TouchableOpacity
                    testID="zoneAccessTooltip"
                    ref={iconRef}
                    onPress={handleToggleTooltip}
                    style={styles.tooltipButton}>
                    <i className="icon-info-circled" style={styles.infoIcon}></i>
                  </TouchableOpacity>
                </View>

                {loading ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator color="#fff" size="large" />
                  </View>
                ) : (
                  <View style={styles.tableContainer}>
                    <View style={styles.tableHeader}>
                      <View style={styles.tableHeaderCell}>
                        <Text style={styles.tableHeaderText}>
                          {i18n.t('zoneAccess.zone')}
                        </Text>
                      </View>
                      <View style={styles.tableHeaderCell}>
                        <Text style={styles.tableHeaderText}>
                          {i18n.t('zoneAccess.zoneAccess')}
                        </Text>
                      </View>
                    </View>
                    {zones.map((zone, index) => {
                      const currentLevel = getAccessLevelForZone(zone.id);
                      const isEvenRow = index % 2 === 0;
                      const firstColColor = isEvenRow ? '#343d48' : '#323943';
                      const secondColColor = isEvenRow ? '#323943' : '#343d48';
                      return (
                        <View key={zone.id} style={styles.tableRow}>
                          <View style={[styles.tableCell, {backgroundColor: firstColColor}]}>
                            <Text style={styles.zoneName}>{zone.name}</Text>
                          </View>
                          <View style={[styles.tableCell, {backgroundColor: secondColColor}]}>
                            <TouchableOpacity
                              onPress={() => handleAccessLevelChange(zone.id)}
                              disabled={saving || loading}
                              style={[
                                styles.accessButton,
                                {
                                  opacity: (saving || loading) ? 0.6 : 1,
                                  borderColor: getAccessLevelBorderColor(currentLevel),
                                  borderWidth: currentLevel === 'always_on' ? 1 : 0
                                }
                              ]}>
                              <LinearGradient
                                colors={getAccessLevelGradient(currentLevel)}
                                start={{x: 0, y: 0}}
                                end={{x: 0, y: 1}}
                                style={styles.accessButtonGradient}>
                                <Text style={[
                                  styles.accessButtonText,
                                  {color: currentLevel === 'disabled' ? '#fff' : '#000'}
                                ]}>
                                  {getAccessLevelLabel(currentLevel)}
                                </Text>
                              </LinearGradient>
                            </TouchableOpacity>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}

                {error ? (
                  <Text style={styles.errorText}>{error}</Text>
                ) : null}

                <TouchableOpacity
                  testID="saveZoneAccessButton"
                  onPress={handleSave}
                  style={[
                    CommonStyles.saveButton,
                    styles.saveButton,
                    {marginTop: 30}
                  ]}
                  disabled={saving || loading}>
                  {saving ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={CommonStyles.saveButtonText}>
                      {i18n.t('zoneAccess.saveAndClose')}
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>
        </TouchableWithoutFeedback>
      </TouchableOpacity>

      {showTooltip && (
        <TouchableWithoutFeedback
          testID="tooltipContainer"
          onPress={() => setShowTooltip(false)}>
          <View style={styles.tooltipModalOverlay}>
            <LinearGradient
              locations={[0, 1]}
              colors={['#8a9daf', '#d7e1e9']}
              style={[
                styles.tooltipContainer,
                {
                  position: 'absolute',
                  top: getTooltipTop(iconPosition),
                  left: getTooltipLeft(iconPosition),
                  width: 300,
                  padding: 10
                }
              ]}>
              <View
                style={[
                  styles.tooltipArrow,
                  {
                    top: -10,
                    left: getTooltipArrowLeft(iconPosition)
                  }
                ]}
              />
              {renderTooltipText()}
            </LinearGradient>
          </View>
        </TouchableWithoutFeedback>
      )}
    </Modal>
  );
};
